import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IMarkdownEditorHeadingSelectProps from "./type";
import type {
    MarkdownEditorHeadingWebPropsType,
    MarkdownEditorHeadingOptionRefs,
    MarkdownEditorHeadingOptionType
} from "./type";
import stylesheet from "./stylesheet";
import {
    useMarkdownEditorHeadingContext,
    MarkdownEditorHeadingContext
} from "./context";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import type {
    MarkdownEditorTextStyle
} from "../../type";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon
} from "lucide-react-native";
import {
    MARKDOWN_EDITOR_TEXT_STYLE_ITEMS,
    MARKDOWN_EDITOR_HEADING_LEVELS,
    getMarkdownEditorHTMLElement
} from "../../util";
import {
    DEFAULT_VARIANTS
} from "../../../markdownViewer/constant";
import PopoverSurface from "../../../popoverSurface";
import AnimatedIcon from "../../../animatedIcon";
import OptionRow from "../../../optionRow";
import Button from "../../../button";
import Text from "../../../text";

const HeadingChevronIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const {
        isOpen
    } = useMarkdownEditorHeadingContext();

    return <AnimatedIcon
        fromIcon={ChevronDownIcon}
        customColor={customColor}
        isTransitioned={isOpen}
        toIcon={ChevronUpIcon}
        iconTransition="morph"
        color={color}
        size={size}
    />;
};

const MarkdownEditorHeadingSelect: FC<IMarkdownEditorHeadingSelectProps> = ({
    isDisabled = false,
    customLocalize,
    returnFocusRef,
    customTheme,
    onChange,
    variants,
    value,
    size
}) => {
    const {
        typography
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        isOpen,
        setIsOpen
    ] = useState<boolean>(false);

    const anchorRef = useRef<View>(null);

    const isOpenRef = useRef<boolean>(false);

    const optionRefs = useRef<MarkdownEditorHeadingOptionRefs>(new Map());

    const activeVariants = {
        ...DEFAULT_VARIANTS,
        ...variants
    };

    const getLabel = (textStyle: MarkdownEditorTextStyle) => {
        if(textStyle === "paragraph") {
            return localize("markdown-editor-paragraph");
        }

        if(textStyle === "code") {
            return localize("markdown-editor-code-block");
        }

        return localize("markdown-editor-heading", [
            String(MARKDOWN_EDITOR_HEADING_LEVELS[textStyle])
        ]);
    };

    const options = MARKDOWN_EDITOR_TEXT_STYLE_ITEMS.map((item): MarkdownEditorHeadingOptionType => {
        const typographyKey = activeVariants[item.variantKey] ?? "bodyLargeSize";

        return {
            meta: `${typographyKey.replace(/Size$/, "")} · ${typography[typographyKey].fontSize}`,
            label: getLabel(item.value),
            typography: typographyKey,
            value: item.value
        };
    });

    const optionsRef = useRef(options);

    const handleSelectRef = useRef<(textStyle: MarkdownEditorTextStyle) => void>(() => undefined);

    const handleClose = () => {
        isOpenRef.current = false;

        setIsOpen(false);
    };

    const handleToggle = () => {
        isOpenRef.current = !isOpen;

        setIsOpen(!isOpen);
    };

    const handleSelect = (textStyle: MarkdownEditorTextStyle) => {
        if(!isOpenRef.current) {
            return;
        }

        handleClose();

        onChange(textStyle);
    };

    useEffect(() => {
        optionsRef.current = options;

        handleSelectRef.current = handleSelect;
    });

    useEffect(() => {
        if(!isOpen || Platform.OS !== "web" || typeof document === "undefined") {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            const entries = optionsRef.current.map((option) => ({
                element: getMarkdownEditorHTMLElement(optionRefs.current.get(option.value)),
                value: option.value
            })).filter((entry) => entry.element !== null);

            const index = entries.findIndex((entry) => entry.element === document.activeElement);

            const current = entries[index];

            if(!current) {
                return;
            }

            if(event.key === "Enter" || event.key === " ") {
                event.preventDefault();

                event.stopPropagation();

                handleSelectRef.current(current.value);

                return;
            }

            const nextIndexes: Record<string, number> = {
                ArrowUp: (index - 1 + entries.length) % entries.length,
                ArrowDown: (index + 1) % entries.length,
                End: entries.length - 1,
                Home: 0
            };

            const nextIndex = nextIndexes[event.key];

            if(nextIndex === undefined) {
                return;
            }

            event.preventDefault();

            entries[nextIndex]?.element?.focus();
        };

        document.addEventListener("keydown", onKeyDown, true);

        return () => {
            document.removeEventListener("keydown", onKeyDown, true);
        };
    }, [
        isOpen
    ]);

    const webProps: MarkdownEditorHeadingWebPropsType = Platform.OS === "web" ? {
        "aria-haspopup": "listbox",
        "aria-expanded": isOpen
    } : {};

    const headingContextValue = useMemo(() => ({
        isOpen
    }), [
        isOpen
    ]);

    const currentLabel = getLabel(value);

    const selectedOption = options.find((option) => option.value === value);

    return <View
        style={stylesheet.container}
        ref={anchorRef}
    >
        <MarkdownEditorHeadingContext.Provider
            value={headingContextValue}
        >
            <Button
                accessibilityLabel={localize("markdown-editor-text-style", [
                    currentLabel
                ])}
                customTheme={customTheme}
                icon={HeadingChevronIcon}
                isDisabled={isDisabled}
                onPress={handleToggle}
                title={currentLabel}
                iconDirection="end"
                variant="ghost"
                type="neutral"
                size={size}
                {...webProps}
            />
        </MarkdownEditorHeadingContext.Provider>
        <PopoverSurface
            returnFocusRef={returnFocusRef}
            customTheme={customTheme}
            onDismiss={handleClose}
            anchorRef={anchorRef}
            isAutoFocused={true}
            isOpen={isOpen}
        >
            <PopoverSurface.List
                aria-label={localize("markdown-editor-text-style-list")}
                customTheme={customTheme}
            >
                {
                    options.map((option) => <OptionRow
                        tabIndex={option.value === (selectedOption?.value ?? options[0]?.value) ? 0 : -1}
                        ref={(node) => {
                            optionRefs.current.set(option.value, node);
                        }}
                        onPress={() => handleSelect(option.value)}
                        isSelected={option.value === value}
                        label={<Text
                            variant={option.typography}
                            customTheme={customTheme}
                        >
                            {option.label}
                        </Text>}
                        customTheme={customTheme}
                        meta={option.meta}
                        key={option.value}
                    />)
                }
            </PopoverSurface.List>
        </PopoverSurface>
    </View>;
};
export default MarkdownEditorHeadingSelect;
